/**
 * Tiny overflow menu used in dialog footers to consolidate
 * secondary actions (Reset / Copy from / Remove override) under
 * a single "⋯" trigger — keeps Cancel + Save the only chrome
 * buttons of normal size.
 *
 * Items are passed as a plain array. Each action callback fires
 * AND auto-closes the menu (saves the caller from threading a
 * close handler). Filtering happens at the call site — if an
 * action isn't applicable in the current scope, omit it from
 * the array rather than disabling it.
 *
 * Closes on outside-click and Esc. Sits above the trigger
 * (bottom-up) since footers usually have no room below.
 */
import { useEffect, useRef, useState } from "react";
import styles from "./MoreActionsMenu.module.css";

export interface MoreAction {
  label: string;
  onClick: () => void;
  /** Tooltip on hover. */
  title?: string;
  /** Greyed-out + non-clickable. Items that should never appear
   *  in this context shouldn't be passed at all; `disabled` is
   *  for items that are temporarily unavailable (e.g., mid-save). */
  disabled?: boolean;
}

interface Props {
  items: MoreAction[];
  /** Hover tooltip on the ⋯ trigger. */
  triggerTitle?: string;
}

export function MoreActionsMenu({ items, triggerTitle }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={triggerTitle ?? "More actions"}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        ⋯
      </button>
      {open && (
        <div className={styles.menu} role="menu">
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              className={styles.item}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                setOpen(false);
              }}
              title={item.title}
              disabled={item.disabled}
              role="menuitem"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
